import { Link } from "@inertiajs/react"

interface EvidenceTypeOption {
  id: number
  name: string
  description: string | null
}

interface EmployeeOption {
  id: number
  name: string
}

interface EvidenceFormData {
  evidence_type_id: number | null
  evidence_date: string
  quantity: number | null
  filename: string
  notes: string
  user_id: number | null
}

interface Props {
  caseId: number
  data: EvidenceFormData
  setData: (key: keyof EvidenceFormData, value: string | number | null) => void
  errors?: Record<string, string[]>
  processing: boolean
  onSubmit: (e: React.FormEvent) => void
  submitLabel: string
  evidence_types: EvidenceTypeOption[]
  can_report_for_others?: boolean
  employees?: EmployeeOption[]
}

export default function EvidenceForm({
  caseId,
  data,
  setData,
  errors,
  processing,
  onSubmit,
  submitLabel,
  evidence_types,
  can_report_for_others,
  employees,
}: Props) {
  return (
    <form onSubmit={onSubmit} className="max-w-3xl">
      {can_report_for_others && employees && (
        <div className="mb-4">
          <label htmlFor="user_id" className="mb-1 block font-medium">
            Employee
          </label>
          <select
            id="user_id"
            value={data.user_id ?? ""}
            onChange={(e) =>
              setData(
                "user_id",
                e.target.value ? parseInt(e.target.value) : null,
              )
            }
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
            required
          >
            <option value="">Select employee...</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.name}
              </option>
            ))}
          </select>
          {errors?.user_id && (
            <p className="text-hpi-red mt-1 text-sm">
              {errors.user_id.join(", ")}
            </p>
          )}
        </div>
      )}

      <div className="mb-4 grid gap-4 md:grid-cols-2">
        <div>
          <label htmlFor="evidence_type_id" className="mb-1 block font-medium">
            Evidence Type
          </label>
          <select
            id="evidence_type_id"
            value={data.evidence_type_id ?? ""}
            onChange={(e) =>
              setData(
                "evidence_type_id",
                e.target.value ? parseInt(e.target.value) : null,
              )
            }
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
          >
            <option value="">Select type...</option>
            {evidence_types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          {errors?.evidence_type_id && (
            <p className="text-hpi-red mt-1 text-sm">
              {errors.evidence_type_id.join(", ")}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="evidence_date" className="mb-1 block font-medium">
            Evidence Date
          </label>
          <input
            type="date"
            id="evidence_date"
            value={data.evidence_date || ""}
            onChange={(e) => setData("evidence_date", e.target.value)}
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
          />
          {errors?.evidence_date && (
            <p className="text-hpi-red mt-1 text-sm">
              {errors.evidence_date.join(", ")}
            </p>
          )}
        </div>
      </div>

      <div className="mb-4 grid gap-4 md:grid-cols-2">
        <div>
          <label htmlFor="quantity" className="mb-1 block font-medium">
            Quantity (mb for video, count for photos)
          </label>
          <input
            type="number"
            id="quantity"
            step="0.01"
            value={data.quantity ?? ""}
            onChange={(e) =>
              setData(
                "quantity",
                e.target.value ? parseFloat(e.target.value) : null,
              )
            }
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
          />
          {errors?.quantity && (
            <p className="text-hpi-red mt-1 text-sm">
              {errors.quantity.join(", ")}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="filename" className="mb-1 block font-medium">
            Filename
          </label>
          <input
            type="text"
            id="filename"
            value={data.filename}
            onChange={(e) => setData("filename", e.target.value)}
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
          />
          {errors?.filename && (
            <p className="text-hpi-red mt-1 text-sm">
              {errors.filename.join(", ")}
            </p>
          )}
        </div>
      </div>

      <div className="mb-4">
        <label htmlFor="notes" className="mb-1 block font-medium">
          Notes
        </label>
        <textarea
          id="notes"
          value={data.notes}
          onChange={(e) => setData("notes", e.target.value)}
          className="border-hpi-light-gray w-full rounded border px-3 py-2"
          rows={4}
        />
        {errors?.notes && (
          <p className="text-hpi-red mt-1 text-sm">{errors.notes.join(", ")}</p>
        )}
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
        >
          {submitLabel}
        </button>
        <Link
          href={`/cases/${caseId}/evidence`}
          className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
        >
          Cancel
        </Link>
      </div>
    </form>
  )
}
